import { createContext } from "react";
import type {
  EntitlementSummary,
  PlanTier,
} from "../services/billingApi";

/** Capabilities the UI can gate on, mirroring the server's `GatedFeature`. */
export type GatedFeature =
  | "REORDER_SECTIONS"
  | "ATS_ENGINE"
  | "PORTFOLIO"
  | "AI_CAREER_AGENT";

export type EntitlementsContextValue = {
  /** Null while loading, or when signed out. */
  entitlements: EntitlementSummary | null;
  isLoading: boolean;
  error: string | null;
  /** Re-read from the server — call after a purchase or a download. */
  refresh: () => Promise<EntitlementSummary | null>;
  /** Client-side hint only; the server re-checks every call. */
  hasFeature: (feature: GatedFeature) => boolean;
  /** Can the signed-in user export this design right now? */
  canDownloadTemplate: (templateKey: string) => boolean;
  /** True when the design itself is locked, regardless of credit balance. */
  isTemplateLocked: (templateKey: string) => boolean;
  planTier: PlanTier;
  /** -1 means unlimited (Ultra Pro). */
  downloadCredits: number;
};

export const EntitlementsContext = createContext<
  EntitlementsContextValue | undefined
>(undefined);

/** Window event the provider listens on to re-read entitlements. */
export const ENTITLEMENTS_CHANGED_EVENT = "resumex:entitlements-changed";

/**
 * Ask the provider to refresh from anywhere — including outside React.
 *
 * Fire this after anything that moves the balance: a completed purchase, a
 * spent download credit, an AI agent query.
 */
export const notifyEntitlementsChanged = (): void => {
  window.dispatchEvent(new CustomEvent(ENTITLEMENTS_CHANGED_EVENT));
};
